import { useEffectWithController } from '@carvana/vdp-react-hooks';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { axios, httpWithAuth } from '~/clients';
import { PRICING } from '~/constants/endpoints';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import ErrorContext from '~/contexts/ErrorContext';
import FinanceAndTradeContext, {
  formatFinanceInfo
} from '~/contexts/FinanceAndTradeContext';
import InventoryContext from '~/contexts/InventoryContext';
import { useHasAppliedTrade } from '~/contexts/SearchContext';
import { useVerboseLogging } from '~/contexts/StaticContext';
import { useUser, useUserLocation } from '~/contexts/UserContext';
import useGenerateAnalyticsData from '~/hooks/useGenerateAnalyticsData';
import useResetInventoryPricing from '~/hooks/useResetInventoryPricing';
import type { PricingResponse } from '~/services/contracts';
import Cookies from '~/utilities/BrowserCookies';
import generateErrorLogger, { logError } from '~/utilities/errorHandlers';

function useShouldFetchPricing(): boolean {
  const user = useUser();
  const { zip } = useUserLocation();
  const {
    inventory: {
      data: {
        inventory: { vehicles }
      }
    }
  } = useContext(InventoryContext);
  const vehiclesNeedFinanceData = useMemo(
    () =>
      // This check handles two cases: 1) LoanTermPricing not being called during
      // SSR, and 2) LoanTermPricing (SSR or CSR) being called but failing
      vehicles.length > 0 &&
      vehicles.every(
        vehicle =>
          !vehicle.price.downPayment &&
          !vehicle.price.monthlyPayment &&
          !vehicle.price.pricingErrorMessage
      ),
    [vehicles]
  );

  return Boolean(user.isLoaded && zip && vehiclesNeedFinanceData);
}

function useGetPricing() {
  const {
    appliedFilters: { finance }
  } = useContext(AppliedFiltersContext);
  const { updateResponseErrors } = useContext(ErrorContext);
  const { setFinanceInfo } = useContext(FinanceAndTradeContext);
  const {
    inventory: {
      data: {
        inventory: { vehicles }
      }
    },
    updateInventoryPricing
  } = useContext(InventoryContext);
  const { downPayment, monthlyPayment } = finance ?? {};
  const hasAppliedTrade = useHasAppliedTrade();
  const user = useUser();
  const { zip } = useUserLocation();
  const analyticsData = useGenerateAnalyticsData();
  const verboseLoggingEnabled = useVerboseLogging();

  const shouldFetchPricing = useShouldFetchPricing();

  const vehicleIds = useMemo(
    () => vehicles.map(vehicle => vehicle.vehicleId),
    [vehicles]
  );

  // Use refs here because we don't want to trigger new API requests when
  // vehicle IDs change.
  const vehicleIdsRef = useRef(vehicleIds);
  useEffect(() => {
    vehicleIdsRef.current = vehicleIds;
  }, [vehicleIds]);

  useResetInventoryPricing(vehicleIds);

  useEffectWithController(
    controller => {
      if (!shouldFetchPricing) return;

      const client = user.data.isLoggedIn ? httpWithAuth : axios;
      const shouldSendFinanceFilters = downPayment || monthlyPayment;
      const financeFilters = shouldSendFinanceFilters
        ? { downPayment, monthlyPayment }
        : undefined;

      client
        .post<PricingResponse>(
          PRICING,
          {
            applyVerboseLogging: verboseLoggingEnabled,
            browserCookieId: Cookies.get('BrowserCookieId'),
            vehicleIds: vehicleIdsRef.current,
            zip5: zip,
            financeFilters,
            analyticsData,
            applyTradeIn: hasAppliedTrade
          },
          { signal: controller.signal }
        )
        .then(({ data }) => {
          if (data.financeInfo.pricingHasErrored) {
            const errorMessage = 'Pricing has errored';
            logError(new Error(errorMessage), { location: 'Pricing' });
            updateResponseErrors({ pricingError: errorMessage });
          } else {
            setFinanceInfo(formatFinanceInfo(data.financeInfo));
            updateInventoryPricing(data);
            updateResponseErrors({ pricingError: null });
          }
        })
        .catch(
          generateErrorLogger(
            'Pricing',
            {},
            {
              callback: () => {
                updateResponseErrors({
                  pricingError: 'Failed to fetch pricing.'
                });
              },
              controller
            }
          )
        );
    },
    [
      analyticsData,
      downPayment,
      hasAppliedTrade,
      monthlyPayment,
      setFinanceInfo,
      shouldFetchPricing,
      updateInventoryPricing,
      updateResponseErrors,
      user.data.isLoggedIn,
      verboseLoggingEnabled,
      zip
    ]
  );
}

export default useGetPricing;
